import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { mfaService } from '../../../services/mfaService';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const MfaSetupModal = ({ isOpen, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [step, setStep] = useState(1); // 1: QR Code, 2: Verify, 3: Backup Codes
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [factorId, setFactorId] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (isOpen && step === 1) {
      enrollMfa();
    }
  }, [isOpen]);

  const enrollMfa = async () => {
    setIsLoading(true);
    setError('');

    const { data, error: enrollError } = await mfaService?.enrollMfa();

    if (enrollError) {
      setError(enrollError?.message || 'Failed to initialize MFA setup');
      setIsLoading(false);
      return;
    }

    setQrCode(data?.qrCode);
    setSecret(data?.secret);
    setFactorId(data?.id);
    setIsLoading(false);
  };

  const handleVerifyCode = async (e) => {
    e?.preventDefault();
    setError('');
    setIsLoading(true);

    if (!verificationCode || verificationCode?.length !== 6) {
      setError('Please enter a valid 6-digit code');
      setIsLoading(false);
      return;
    }

    const { error: verifyError } = await mfaService?.verifyEnrollment(
      factorId,
      verificationCode
    );

    if (verifyError) {
      setError(verifyError?.message || 'Invalid verification code');
      setIsLoading(false);
      return;
    }

    // Generate backup codes
    const { data: codes, error: codesError } = await mfaService?.generateBackupCodes(user?.id);

    if (codesError) {
      setError('MFA enabled but failed to generate backup codes');
      setIsLoading(false);
      return;
    }

    setBackupCodes(codes);
    setStep(3);
    setIsLoading(false);
  };

  const handleCopySecret = () => {
    navigator?.clipboard?.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyBackupCodes = () => {
    const codesText = backupCodes?.join('\n');
    navigator?.clipboard?.writeText(codesText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleComplete = () => {
    onSuccess?.();
    onClose?.();
    setStep(1);
    setVerificationCode('');
    setError('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-elevation-3 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-2xl font-bold text-foreground">
              {step === 1 && 'Set Up Two-Factor Authentication'}
              {step === 2 && 'Verify Your Code'}
              {step === 3 && 'Save Your Backup Codes'}
            </h2>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-smooth"
            >
              <Icon name="X" size={24} />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-error/10 border border-error rounded-md flex items-start space-x-2">
              <Icon name="AlertCircle" size={18} color="var(--color-error)" />
              <p className="font-body text-sm text-error flex-1">{error}</p>
            </div>
          )}

          {/* Step 1: QR Code */}
          {step === 1 && (
            <div className="space-y-4">
              <p className="font-body text-sm text-muted-foreground">
                Scan this QR code with your authenticator app (Google Authenticator, Authy, 1Password, etc.)
              </p>

              {isLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                </div>
              ) : (
                <>
                  {qrCode && (
                    <div className="flex justify-center p-4 bg-white rounded-lg">
                      <img src={qrCode} alt="MFA QR Code" className="w-48 h-48" />
                    </div>
                  )}

                  <div className="space-y-2">
                    <p className="font-body text-xs text-muted-foreground">
                      Or enter this code manually:
                    </p>
                    <div className="flex items-center space-x-2">
                      <code className="flex-1 p-2 bg-muted rounded font-mono text-sm text-foreground break-all">
                        {secret}
                      </code>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleCopySecret}
                        iconName={copied ? 'Check' : 'Copy'}
                      >
                        {copied ? 'Copied' : 'Copy'}
                      </Button>
                    </div>
                  </div>

                  <Button
                    fullWidth
                    onClick={() => setStep(2)}
                    iconName="ArrowRight"
                    iconPosition="right"
                  >
                    Continue
                  </Button>
                </>
              )}
            </div>
          )}

          {/* Step 2: Verify Code */}
          {step === 2 && (
            <form onSubmit={handleVerifyCode} className="space-y-4">
              <p className="font-body text-sm text-muted-foreground">
                Enter the 6-digit code from your authenticator app to verify the setup.
              </p>

              <Input
                type="text"
                label="Verification Code"
                placeholder="000000"
                value={verificationCode}
                onChange={(e) => {
                  const value = e?.target?.value?.replace(/\D/g, '')?.slice(0, 6);
                  setVerificationCode(value);
                  setError('');
                }}
                maxLength={6}
                required
              />

              <div className="flex space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  fullWidth
                  onClick={() => setStep(1)}
                  iconName="ArrowLeft"
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  fullWidth
                  loading={isLoading}
                  disabled={isLoading || verificationCode?.length !== 6}
                  iconName="Check"
                >
                  Verify
                </Button>
              </div>
            </form>
          )}

          {/* Step 3: Backup Codes */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="p-4 bg-warning/10 border border-warning rounded-md">
                <div className="flex items-start space-x-2">
                  <Icon name="AlertTriangle" size={18} color="var(--color-warning)" />
                  <div className="flex-1">
                    <p className="font-body text-sm font-semibold text-warning mb-1">
                      Save These Backup Codes
                    </p>
                    <p className="font-body text-xs text-muted-foreground">
                      Store these codes in a safe place. You can use them to access your account if you lose your authenticator device.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2 p-4 bg-muted rounded-lg">
                  {backupCodes?.map((code, index) => (
                    <code key={index} className="font-mono text-sm text-foreground">
                      {code}
                    </code>
                  ))}
                </div>
                <Button
                  variant="outline"
                  fullWidth
                  onClick={handleCopyBackupCodes}
                  iconName={copied ? 'Check' : 'Copy'}
                >
                  {copied ? 'Copied to Clipboard' : 'Copy All Codes'}
                </Button>
              </div>

              <Button
                fullWidth
                onClick={handleComplete}
                iconName="Check"
              >
                Complete Setup
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MfaSetupModal;